// Stepped watch phases (content/README "Launch types · Memory / Word": a watch phase whose board is a
// board.sequence shows its items one after another, each for durationMs / items, with a step counter):
// Digit Span's digits in one big tile (board 17 M6a), Tap Order's frames as one square lit at a time on
// the grid (M5a: every item a `cells` glyph with one fill), Mental Juggle's numbers likewise one by one.
// In play the step advances on a timer (one interval tick per step, no per-frame work) and a polite live
// region announces each step; previews (admin) keep the static board with every item shown at once.
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { GlyphView, puzzleColor } from "../glyphs";
import type { Board, CellsGlyph, Glyph, SequenceBoard } from "../types";
import { kindOf } from "../types";
import styles from "./launch.module.css";

const ROW_LETTERS = "ABCDEFGHIJKL";
const textOf = (g: unknown) => (kindOf(g) === "text" ? String((g as { value?: unknown }).value ?? "").trim() : "");

type Stepped =
  | { kind: "items"; items: Glyph[]; digits: boolean }
  | { kind: "lights"; rows: number; cols: number; order: { at: [number, number]; color: string }[] };

/** Frames of one n × n `cells` glyph each with exactly one filled square: the squares, in order. */
function lightFrames(items: Glyph[]): Extract<Stepped, { kind: "lights" }> | null {
  if (!items.every((g) => kindOf(g) === "cells")) return null;
  const first = items[0] as CellsGlyph;
  const rows = Math.trunc(Number(first.rows));
  const cols = Math.trunc(Number(first.cols));
  if (!(rows >= 1 && rows <= 12 && cols >= 1 && cols <= 12)) return null;
  const order: { at: [number, number]; color: string }[] = [];
  for (const g of items as CellsGlyph[]) {
    const fills = Array.isArray(g.fills) ? g.fills : [];
    if (Math.trunc(Number(g.rows)) !== rows || Math.trunc(Number(g.cols)) !== cols || fills.length !== 1 || !Array.isArray(fills[0]?.at)) return null;
    order.push({ at: [Math.trunc(Number(fills[0]!.at[0])), Math.trunc(Number(fills[0]!.at[1]))], color: puzzleColor(fills[0]!.color, "violet") });
  }
  return { kind: "lights", rows, cols, order };
}

/** The stepped form of a watch board: a board.sequence of 2+ items. Null for anything else. */
export function steppedWatch(board: Board | undefined): Stepped | null {
  if (kindOf(board) !== "sequence") return null;
  const items = (Array.isArray((board as SequenceBoard).items) ? (board as SequenceBoard).items : []).filter((g) => g && typeof g === "object");
  if (items.length < 2) return null;
  return lightFrames(items) ?? { kind: "items", items, digits: items.every((g) => /^\d$/.test(textOf(g))) };
}

function useStep(n: number, durationMs: number): number {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (n <= 1) return;
    const per = Math.max(200, durationMs / n);
    const id = window.setInterval(() => setStep((s) => (s + 1 < n ? s + 1 : s)), per);
    return () => window.clearInterval(id);
  }, [n, durationMs]);
  return Math.min(step, Math.max(0, n - 1));
}

/** Digit Span and Mental Juggle (board 17 M6a, M7a): the current item large in an accent tile, dots, "Digit 4 of 7". */
function SteppedItems({ items, digits, durationMs }: { items: Glyph[]; digits: boolean; durationMs: number }) {
  const step = useStep(items.length, durationMs);
  const item = items[step];
  const text = textOf(item);
  const noun = digits ? "Digit" : "Item";
  return (
    <div className={styles.stepPanel} data-stepped="items" data-step={step}>
      <div className={styles.stepTile} aria-live="polite" aria-atomic="true">
        <span className={styles.srOnly}>{`${noun} ${step + 1} of ${items.length}: `}</span>
        {text ? (
          <span className={styles.stepDigit} data-long={text.length > 2 || undefined}>
            {text}
          </span>
        ) : (
          <span className={styles.stepGlyph}>
            <GlyphView glyph={item} />
          </span>
        )}
      </div>
      <div className={styles.stepDots} aria-hidden>
        {items.map((_, i) => (
          <span key={i} data-state={i < step ? "seen" : i === step ? "now" : "next"} />
        ))}
      </div>
      <span className={styles.stepCount} aria-hidden>
        {noun} {step + 1} of {items.length}
      </span>
    </div>
  );
}

/** Tap Order (board 17 M5a): a plain grid, one square lit at a time, "Step 4 of 8". */
function SteppedLights({ rows, cols, order, durationMs }: { rows: number; cols: number; order: { at: [number, number]; color: string }[]; durationMs: number }) {
  const step = useStep(order.length, durationMs);
  const now = order[step]!;
  const [lr, lc] = now.at;
  return (
    <div className={styles.stepLights} data-stepped="lights" data-step={step}>
      <div className={styles.stepGrid} style={{ "--cols": cols, "--lit": now.color } as CSSProperties} aria-hidden>
        {Array.from({ length: rows * cols }, (_, i) => (
          <span key={i} className={styles.stepCell} data-lit={(Math.floor(i / cols) === lr && i % cols === lc) || undefined} />
        ))}
      </div>
      <span className={styles.srOnly} aria-live="polite" aria-atomic="true">
        {`Step ${step + 1} of ${order.length}: row ${ROW_LETTERS[lr] ?? lr + 1}, column ${lc + 1} lights`}
      </span>
      <span className={styles.stepCount} aria-hidden>
        Step {step + 1} of {order.length}
      </span>
    </div>
  );
}

/** The watch board, stepped when `active` (play) and it is a board.sequence; otherwise `children`. */
export function LaunchWatch({ active, board, durationMs, children }: { active: boolean; board: Board | undefined; durationMs: number | undefined; children: ReactNode }) {
  const stepped = active ? steppedWatch(board) : null;
  const ms = typeof durationMs === "number" && Number.isFinite(durationMs) ? durationMs : 4000;
  if (stepped?.kind === "items") return <SteppedItems items={stepped.items} digits={stepped.digits} durationMs={ms} />;
  if (stepped?.kind === "lights") return <SteppedLights rows={stepped.rows} cols={stepped.cols} order={stepped.order} durationMs={ms} />;
  return <>{children}</>;
}
